import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TrackerService } from '@app/shared/services/tracker.service';
import { camelCaseKeys } from '@app/shared/utils/property';
import { WindowToken } from '@app/shared/window.token';
import { TagsApi } from '@app/tags/tag-api.model';
import { getTagCompletedRatings } from '@app/shared/utils/tag-helpers';
import { Profile } from '@app/profile/profile.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'dgx-profile-skills-list-other-ratings',
  templateUrl: './profile-skills-list-other-ratings.component.html',
  styleUrls: ['./profile-skills-list-other-ratings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSkillsListOtherRatingsComponent implements OnChanges {
  @Input() public tag: TagsApi.TagDetails;
  @Input() public isOwner: boolean;
  @Input() public profile: Profile;

  @ViewChild('popoverTrigger') public popoverTriggerRef: ElementRef;

  public showRatings: boolean = false;
  public completedRatings: TagsApi.UserTagRating[];
  public moreRatingsCount: number;
  public hasMoreRatings: boolean;

  private popoverViewedTimeout;
  private popoverViewedDelay = 1000;

  constructor(
    private trackerService: TrackerService,
    private translateService: TranslateService,
    @Inject(WindowToken) private windowRef: Window
  ) {}

  public ngOnChanges({ tag }: SimpleChanges): void {
    if (tag.currentValue) {
      // TODO: remove camelCaseKeys after api calls start using ngxHttpClient
      this.tag = camelCaseKeys(tag.currentValue);
      this.setState(this.tag);
    }
  }

  public togglePopover(showRatings: boolean): void {
    this.showRatings = showRatings;
    if (showRatings) {
      // Delay notification of `popoverViewed` so random mousenter events aren't tracked
      this.popoverViewedTimeout = this.windowRef.setTimeout(
        () => this.popoverViewed(),
        this.popoverViewedDelay
      );
    } else {
      this.windowRef.clearTimeout(this.popoverViewedTimeout);
    }
  }

  public popoverViewed(): void {
    let properties: any = {
      RenderMethod: 'Profile Skills List',
      SkillId: this.tag.tagId,
      SkillName: this.tag.name,
      SkillOwnerUserId: this.profile.id,
    };
    if (this.isOwner) {
      properties = {
        ...properties,
        IsFocusSkill: this.tag.requestingUserIsFocused,
        IsOnProfile: this.tag.isFollowing,
      };
    } else {
      properties = {
        ...properties,
        IsFocusSkillForOwner: (this.tag as any).isFocusSkillForOwner,
        IsOnProfileForOwner: (this.tag as any).isOnProfileForOwner,
      };
    }
    this.trackerService.trackEventData({
      action: 'Skill Other Signals Previewed',
      properties,
    });
  }

  private setState(tag: TagsApi.TagDetails): void {
    this.completedRatings = getTagCompletedRatings(tag, true);
    this.moreRatingsCount = this.completedRatings.length;
    this.hasMoreRatings = this.moreRatingsCount > 0;
  }

  public get ariaLabel(): string {
    let label = '';
    if (this.completedRatings && this.completedRatings.length > 0) {
      label = this.translateService.instant(
        'dgTagRating_A11yTotalRatingCounts',
        {
          skillName: this.tag.title,
          totalRatingsCount: this.completedRatings.length,
        }
      );
    }
    return label;
  }
}
